/**
 * @fileoverview Configuración centralizada de la API
 * @description URLs, configuración y utilidades para todas las llamadas a la API
 */

// URL base de la API
// En producción usa REACT_APP_API_PROD_URL, en desarrollo usa REACT_APP_API_DEV_URL
export const API_BASE_URL = process.env.REACT_APP_API_PROD_URL || process.env.REACT_APP_API_DEV_URL || 'http://localhost:8080/api';

// Endpoints específicos
export const API_ENDPOINTS = {
  SOCIEDAD: '/formulario/sociedad',
  ACCION_FORMATIVA: '/formulario/accion-formativa',
  CURSO: '/formulario/curso',
  POLIZA: '/formulario/poliza',
  UPLOAD_PDF: '/formulario/upload-pdf',
  VALIDATE_CAPTCHA: '/validate-captcha'
};

// Configuración de timeouts y límites
export const API_CONFIG = {
  TIMEOUT: 30000, // 30 segundos
  MAX_FILE_SIZE: 20 * 1024 * 1024, // 20MB
  RETRY_ATTEMPTS: 3
};

/**
 * Configuración por defecto para las peticiones HTTP
 */
export const defaultConfig: RequestInit = {
  headers: {
    'Content-Type': 'application/json',
  },
};

/**
 * Maneja errores de respuesta HTTP
 * @param response - Respuesta de la API
 * @throws Error si la respuesta no es exitosa
 */
export const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Utilidades para manejo de errores de API
 */
export const apiUtils = {
  /**
   * Maneja errores de conexión
   * @param error - Error capturado
   * @returns Mensaje de error amigable
   */
  handleError(error: any): string {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return 'No se pudo conectar con el servidor. Verifique su conexión.';
    }
    return error.message || 'Ha ocurrido un error inesperado';
  },
};

// Función para construir URL completa
export const buildApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

// Función para verificar si el servidor está disponible
export const checkServerHealth = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    // Solo log en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.warn('Servidor no disponible:', error);
    }
    return false;
  }
};

/**
 * Configuración específica para subida de archivos
 */
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: API_CONFIG.MAX_FILE_SIZE,
  ALLOWED_TYPES: ['application/pdf'],
  ALLOWED_EXTENSIONS: ['.pdf']
};

/**
 * Headers específicos para subida de archivos
 */
export const getUploadHeaders = (): HeadersInit => {
  return {
    // No incluir Content-Type para permitir que el navegador lo establezca automáticamente
    // con el boundary correcto para multipart/form-data
  };
};
